/**
 * GroundTruth Bulk Editor chat state — proposal cards and choice cards.
 *
 * Pure reducers over three keys of the bulk-editor hook's platform state:
 *
 *   proposals     [{ previewId, preview, status, ts, decidedAt, detail }]
 *                 every preview the agent proposed, oldest first, each keeping
 *                 its full snapshot after it is confirmed, cancelled, replaced
 *                 or undone. The card for one is rendered from `preview`, so a
 *                 confirmed proposal no longer vanishes from the chat.
 *   choiceSets    [{ choice_set_id, kind, question, choices, status, resolution,
 *                    ts, decidedAt }]
 *                 the agent's structured clarification questions.
 *   choiceNotices [{ key, ts, choiceSetId, reason }]
 *                 local "that didn't pick an option" notes for a typed reply
 *                 the resolver refused. Never sent anywhere.
 *
 * GroundTruth only — the hook reduces into these keys only when the session's
 * platform is 'groundtruth'. The backend mirror is
 * services/bulkEdit/chatHistory.js; the transition table below matches it, so a
 * card cannot show a status the server would refuse.
 *
 * `ts` is when the item appeared and `decidedAt` when it stopped awaiting the
 * user; gtChatFeed anchors each card's chronological position on the two.
 */

export const GT_CHAT_INITIAL = Object.freeze({
  proposals: [],
  choiceSets: [],
  choiceNotices: [],
});

/** Text for each status — the card never relies on colour alone to say it. */
export const PROPOSAL_STATUS_LABELS = Object.freeze({
  pending: 'Awaiting your confirmation',
  applying: 'Applying…',
  staged: 'Applying…',
  saved: 'Applied and saved',
  save_failed: 'Save failed',
  cancelled: 'Cancelled',
  undone: 'Undone',
  superseded: 'Replaced by a newer proposal',
  expired: 'Expired — not applied',
  unconfirmed: 'Save not confirmed',
});

/** Mirrors ENTER_FROM in services/bulkEdit/chatHistory.js. */
const ENTER_FROM = {
  applying: ['pending'],
  staged: ['pending', 'applying'],
  saved: ['staged', 'applying', 'unconfirmed', 'save_failed'],
  save_failed: ['staged', 'applying', 'unconfirmed'],
  undone: ['saved'],
  cancelled: ['pending', 'applying'],
  superseded: ['pending', 'applying'],
  expired: ['pending', 'applying'],
  unconfirmed: ['staged'],
};

const UNDECIDED = new Set(['pending', 'applying']);

const proposalsOf = (state) => (Array.isArray(state?.proposals) ? state.proposals : []);
const choiceSetsOf = (state) => (Array.isArray(state?.choiceSets) ? state.choiceSets : []);

/** Close every open choice set (a newer question, a proposal, a free-form reply). */
export function closeOpenChoiceSets(sets, at = Date.now(), status = 'superseded') {
  let changed = false;
  const next = (sets || []).map((s) => {
    if (s.status !== 'open') return s;
    changed = true;
    return { ...s, status, decidedAt: s.decidedAt ?? at };
  });
  return changed ? next : (sets || []);
}

/**
 * A new preview arrived. Older undecided proposals are superseded (only the
 * latest pending one keeps live controls) and any open question closes — the
 * agent answered it with a concrete proposal.
 */
export function addProposal(state, preview, ts = Date.now()) {
  if (!preview?.preview_id) return {};
  const id = String(preview.preview_id);
  const prior = proposalsOf(state);
  // A replayed event for a card we already have changes nothing.
  if (prior.some((p) => p.previewId === id)) return {};
  const proposals = prior.map((p) => (UNDECIDED.has(p.status)
    ? {
      ...p,
      status: 'superseded',
      decidedAt: p.decidedAt ?? ts,
      detail: 'A newer proposal replaced this one.',
    }
    : p));
  proposals.push({ previewId: id, preview, status: 'pending', ts, decidedAt: null, detail: null });
  return { proposals, choiceSets: closeOpenChoiceSets(choiceSetsOf(state), ts) };
}

export function findProposal(state, previewId) {
  return proposalsOf(state).find((p) => p.previewId === String(previewId)) || null;
}

/**
 * Move a proposal to `status` if the transition is allowed; otherwise a no-op
 * patch. `decidedAt` is stamped whenever it leaves `pending` — including a
 * restored card whose earlier approval was handed back, so it anchors where it
 * was actually decided this time.
 */
export function setProposalStatus(state, previewId, status, { detail, at = Date.now() } = {}) {
  const current = findProposal(state, previewId);
  if (!current) return {};
  if (current.status === status) {
    // Same status, new explanation — only an EXPLICIT detail rewrites it.
    const next = detail === undefined ? current.detail : (detail || null);
    if (next === current.detail) return {};
    return {
      proposals: proposalsOf(state).map((p) => (p.previewId === current.previewId ? { ...p, detail: next } : p)),
    };
  }
  if (!(ENTER_FROM[status] || []).includes(current.status)) return {};
  return {
    proposals: proposalsOf(state).map((p) => (p.previewId === current.previewId
      ? {
        ...p,
        status,
        detail: detail || null,
        decidedAt: p.status === 'pending' ? at : p.decidedAt,
      }
      : p)),
  };
}

/**
 * The named undecided proposals become expired — the ids the server says it
 * dropped. Clears the active preview if it was one of them.
 */
export function expireProposals(state, previewIds, detail = null, at = Date.now()) {
  const ids = new Set((previewIds || []).map(String));
  const list = proposalsOf(state);
  if (!list.some((p) => ids.has(p.previewId) && UNDECIDED.has(p.status))) return {};
  return {
    proposals: list.map((p) => (ids.has(p.previewId) && UNDECIDED.has(p.status)
      ? { ...p, status: 'expired', detail, decidedAt: p.status === 'pending' ? at : p.decidedAt }
      : p)),
    ...(state?.activePreview && ids.has(String(state.activePreview.preview_id)) ? { activePreview: null } : {}),
  };
}

/**
 * Every undecided proposal (pending / applying) becomes expired — the server
 * dropped their previews (the row set changed under them). Clears the active
 * preview with them. A no-op patch when nothing was undecided.
 */
export function expireUndecided(state, detail = null, at = Date.now()) {
  const list = proposalsOf(state);
  if (!list.some((p) => UNDECIDED.has(p.status))) return {};
  return {
    proposals: list.map((p) => (UNDECIDED.has(p.status)
      ? { ...p, status: 'expired', detail, decidedAt: p.status === 'pending' ? at : p.decidedAt }
      : p)),
    activePreview: null,
  };
}

/** The proposal whose Confirm button is live, if any. */
export function latestPendingProposal(state) {
  const list = proposalsOf(state);
  const last = list[list.length - 1];
  return last && last.status === 'pending' ? last : null;
}

/** A new choice card. Older open ones close — only the latest can be answered. */
export function addChoiceSet(state, set, ts = Date.now()) {
  if (!set?.choice_set_id) return {};
  const prior = choiceSetsOf(state);
  if (prior.some((s) => s.choice_set_id === set.choice_set_id)) return {};
  // The server may already have closed it by the time the event is written (a
  // reply landed first, or another tab answered it) — keep its word: shown
  // read-only, and it closes nothing.
  if (set.status && set.status !== 'open') {
    return {
      choiceSets: [...prior, {
        ...set,
        choices: (set.choices || []).slice(0, 5),
        ts,
        decidedAt: Number.isFinite(set.decided_at) ? set.decided_at : ts,
      }],
    };
  }
  const closed = closeOpenChoiceSets(prior, ts);
  return {
    choiceSets: [...closed, {
      ...set,
      choices: (set.choices || []).slice(0, 5),
      status: 'open',
      resolution: null,
      ts,
      decidedAt: null,
    }],
  };
}

export function latestOpenChoiceSet(state) {
  const sets = choiceSetsOf(state);
  for (let i = sets.length - 1; i >= 0; i--) {
    if (sets[i].status === 'open') return sets[i];
  }
  return null;
}

/** Mark a choice card answered — after the pick reached the agent. */
export function resolveChoiceSet(state, choiceSetId, choice, via, at = Date.now()) {
  return {
    choiceSets: choiceSetsOf(state).map((s) => (s.choice_set_id === choiceSetId
      ? {
        ...s,
        status: 'resolved',
        decidedAt: s.decidedAt ?? at,
        resolution: { choice_id: choice.id, number: choice.number, label: choice.label, via, at },
      }
      : s)),
  };
}

/** Rebuild the cards from a create/resume response's `chatHistory`. */
export function restoreChatHistory(chatHistory) {
  if (!chatHistory || typeof chatHistory !== 'object') return null;
  const proposals = (Array.isArray(chatHistory.proposals) ? chatHistory.proposals : [])
    .filter((e) => e?.preview_id && e?.snapshot)
    .map((e) => ({
      previewId: String(e.preview_id),
      preview: { ...e.snapshot, preview_id: String(e.preview_id) },
      status: e.status || 'expired',
      ts: Number.isFinite(e.created_at) ? e.created_at : null,
      decidedAt: Number.isFinite(e.decided_at) ? e.decided_at : null,
      detail: e.detail || null,
      restored: true,
    }));
  const choiceSets = (Array.isArray(chatHistory.choiceSets) ? chatHistory.choiceSets : [])
    .filter((s) => s?.choice_set_id)
    .map((s) => ({
      ...s,
      choices: (s.choices || []).slice(0, 5),
      ts: Number.isFinite(s.created_at) ? s.created_at : null,
      decidedAt: Number.isFinite(s.decided_at) ? s.decided_at : null,
      restored: true,
    }));
  return { proposals, choiceSets, choiceNotices: [] };
}
